import { apiClient } from '../../lib/api/apiClient';
import { readEveryPage } from '../../lib/api/everyPage';
import { pathWithQuery } from '../../lib/api/queryString';
import {
    ApiContractError,
    type ListResponse,
    requireArray,
    requireBoolean,
    requireBusinessDate,
    requireInstant,
    requireInteger,
    requireNullableString,
    requireOneOf,
    requirePresent,
    requireRecord,
    requireString,
    toListResponse,
} from '../../lib/api/wire';
import {
    ACTIVITY_BUCKETS,
    ACTIVITY_RECORD_TYPES,
    type ActivitiesListParams,
    type ActivityCounts,
    type ActivityCreateRequest,
    type ActivityDetail,
    type ActivityDone,
    type ActivityItem,
    type ActivityPatchRequest,
    type ActivityRecord,
    type ActivityRecordRef,
    type ActivityScope,
    type ActivityStatusChangeRequest,
    type ActivityType,
    type ActivityUser,
    type AssigneeCandidate,
} from './types';

/**
 * The follow-up reads and the create (`.forge/plans/F3-a/F3-a-Wave-1-contract.md`, frozen): row 1
 * `GET /api/v1/activity-types`, row 2 `GET /api/v1/activities`, row 3 `GET /api/v1/activities/counts`,
 * row 4 `GET /api/v1/activities/{id}`, row 5 `POST /api/v1/activities` and row 6
 * `GET /api/v1/activities/assignee-candidates`; and the writes of `.forge/plans/F3-a/F3-a-Wave-2-contract.md`
 * (frozen): row 7 `PATCH /api/v1/activities/{id}`, rows 8 and 9 `POST …/{id}/complete` and `…/{id}/reopen`,
 * and row 10 `DELETE /api/v1/activities/{id}?version=<n>`. Rows 7–9 answer wave 1's `ActivityDetail`
 * (F3-a Wave 2 contract D-1). Every body
 * is narrowed from `unknown` with the shared `src/lib/api/wire.ts` helpers — no unchecked cast — so a
 * shape divergence fails here as an `ApiContractError` naming the member (F3-a WI-1.2 plan Decision 7). Each
 * narrower builds a new object with exactly the members the contract names; anything else is dropped.
 *
 * Every request sends only the parameters its row declares (F3-a WI-1.2 plan Decision 8): the server answers any other
 * with `400 UNKNOWN_FIELD`, `sort` and `q` included (F3-a Wave 1 contract D-4).
 */

export const ACTIVITY_TYPES_PATH = '/api/v1/activity-types';
export const ACTIVITIES_PATH = '/api/v1/activities';
export const ACTIVITY_COUNTS_PATH = `${ACTIVITIES_PATH}/counts`;
export const ASSIGNEE_CANDIDATES_PATH = `${ACTIVITIES_PATH}/assignee-candidates`;

/** The page size the Activities page asks for. */
export const ACTIVITIES_PAGE_SIZE = 25;

/** The largest page row 6 serves; the create dialog reads the candidates in pages of it. */
export const ASSIGNEE_CANDIDATES_SIZE = 100;

/**
 * Row 2's path. `page` and `size` always; `view` only when `completed`; `scope` only when `reach`;
 * `bucket` only with `view=open` and a bucket chosen. Never `sort`, `q`, `accountId` or `leadId` in
 * wave 1.
 */
export function buildActivitiesPath(params: ActivitiesListParams): string {
    return pathWithQuery(ACTIVITIES_PATH, [
        ['page', params.page],
        ['size', params.size],
        ['view', params.view === 'completed' ? 'completed' : undefined],
        ['scope', params.scope === 'reach' ? 'reach' : undefined],
        ['bucket', params.view === 'open' ? (params.bucket ?? undefined) : undefined],
    ]);
}

/** Row 3's path: `scope` only, and only when `reach` (F3-a Wave 1 contract D-6). */
export function buildCountsPath(scope: ActivityScope): string {
    return pathWithQuery(ACTIVITY_COUNTS_PATH, [['scope', scope === 'reach' ? 'reach' : undefined]]);
}

/** Row 6's path: exactly one of `accountId` / `leadId`, `page` after the first, and `size=100`; never `q`. */
export function buildCandidatesPath(record: ActivityRecordRef, page = 1): string {
    return pathWithQuery(ASSIGNEE_CANDIDATES_PATH, [
        ['accountId', record.type === 'account' ? record.id : undefined],
        ['leadId', record.type === 'lead' ? record.id : undefined],
        ['page', page > 1 ? page : undefined],
        ['size', ASSIGNEE_CANDIDATES_SIZE],
    ]);
}

const HOUR_MINUTE = /^([01]\d|2[0-3]):[0-5]\d$/;

/** `dueTime`: `HH:mm`, 24-hour, no seconds — or `null`. An omitted key is refused. */
function requireNullableTime(value: unknown, field: string): string | null {
    const text = requireNullableString(value, field);
    if (text !== null && !HOUR_MINUTE.test(text)) {
        throw new ApiContractError(`${field} must be HH:mm or null`);
    }
    return text;
}

/** One `ActivityType` — exactly `{ id, key, label }`. */
export function toActivityType(value: unknown, field: string): ActivityType {
    const type = requireRecord(value, field);
    return {
        id: requireInteger(type.id, `${field}.id`),
        key: requireString(type.key, `${field}.key`),
        label: requireString(type.label, `${field}.label`),
    };
}

/** One `ActivityUser` — exactly `{ id, displayName, active }`. */
export function toActivityUser(value: unknown, field: string): ActivityUser {
    const user = requireRecord(value, field);
    return {
        id: requireInteger(user.id, `${field}.id`),
        displayName: requireNullableString(user.displayName, `${field}.displayName`),
        active: requireBoolean(user.active, `${field}.active`),
    };
}

function toActivityRecord(value: unknown, field: string): ActivityRecord {
    const record = requireRecord(value, field);
    return {
        type: requireOneOf(record.type, `${field}.type`, ACTIVITY_RECORD_TYPES),
        id: requireInteger(record.id, `${field}.id`),
        name: requireString(record.name, `${field}.name`),
    };
}

function toActivityDone(value: unknown, field: string): ActivityDone | null {
    requirePresent(value, field);
    if (value === null) {
        return null;
    }
    const done = requireRecord(value, field);
    return {
        at: requireInstant(done.at, `${field}.at`),
        by: toActivityUser(done.by, `${field}.by`),
    };
}

/** One `ActivityItem` — exactly the eleven members. */
export function toActivityItem(value: unknown, field: string): ActivityItem {
    const item = requireRecord(value, field);
    return {
        id: requireInteger(item.id, `${field}.id`),
        type: toActivityType(item.type, `${field}.type`),
        title: requireString(item.title, `${field}.title`),
        dueDate: requireBusinessDate(item.dueDate, `${field}.dueDate`),
        dueTime: requireNullableTime(item.dueTime, `${field}.dueTime`),
        bucket: requireOneOf(item.bucket, `${field}.bucket`, ACTIVITY_BUCKETS),
        assignee: toActivityUser(item.assignee, `${field}.assignee`),
        record: toActivityRecord(item.record, `${field}.record`),
        done: toActivityDone(item.done, `${field}.done`),
        needsReassigning: requireBoolean(item.needsReassigning, `${field}.needsReassigning`),
        version: requireInteger(item.version, `${field}.version`),
    };
}

/** One `ActivityDetail` — the item's eleven members plus `notes`, `createdAt`, `createdBy`, `modifiedAt`. */
export function toActivityDetail(value: unknown, field: string): ActivityDetail {
    const detail = requireRecord(value, field);
    requirePresent(detail.createdBy, `${field}.createdBy`);
    return {
        ...toActivityItem(detail, field),
        notes: requireNullableString(detail.notes, `${field}.notes`),
        createdAt: requireInstant(detail.createdAt, `${field}.createdAt`),
        createdBy: detail.createdBy === null ? null : toActivityUser(detail.createdBy, `${field}.createdBy`),
        modifiedAt: requireInstant(detail.modifiedAt, `${field}.modifiedAt`),
    };
}

/** Row 3's body — exactly three integers. */
export function toActivityCounts(value: unknown): ActivityCounts {
    const counts = requireRecord(value, 'the body');
    return {
        overdue: requireInteger(counts.overdue, 'overdue'),
        today: requireInteger(counts.today, 'today'),
        upcoming: requireInteger(counts.upcoming, 'upcoming'),
    };
}

/** One `AssigneeCandidate` — exactly `{ id, displayName, roleName }`. */
export function toAssigneeCandidate(value: unknown, field: string): AssigneeCandidate {
    const candidate = requireRecord(value, field);
    return {
        id: requireInteger(candidate.id, `${field}.id`),
        displayName: requireNullableString(candidate.displayName, `${field}.displayName`),
        roleName: requireString(candidate.roleName, `${field}.roleName`),
    };
}

/**
 * Row 5's body: exactly the members the request sets, in the contract's order. `dueTime` and `notes` are
 * left out when not set (F3-a WI-1.2 plan Decision 8) — the server reads absent and `null` the same.
 * `assigneeId` is sent whenever the request carries one, and the dialog's always does: `toCreateRequest`
 * sends the caller when no one else was chosen.
 */
export function toCreateBody(request: ActivityCreateRequest): Record<string, string | number> {
    const body: Record<string, string | number> = {};
    if ('accountId' in request) {
        body.accountId = request.accountId;
    } else {
        body.leadId = request.leadId;
    }
    body.typeId = request.typeId;
    body.title = request.title;
    body.dueDate = request.dueDate;
    if (request.dueTime !== undefined && request.dueTime !== '') {
        body.dueTime = request.dueTime;
    }
    if (request.notes !== undefined && request.notes !== '') {
        body.notes = request.notes;
    }
    if (request.assigneeId !== undefined) {
        body.assigneeId = request.assigneeId;
    }
    return body;
}

/** Row 1: the active types in `sort_order`, a top-level array (F3-a Wave 1 contract D-2). */
export async function listActivityTypes(): Promise<readonly ActivityType[]> {
    const body = await apiClient.get<unknown>(ACTIVITY_TYPES_PATH);
    return requireArray(body, 'the body', toActivityType);
}

/** Row 2: one page of follow-ups. */
export async function listActivities(params: ActivitiesListParams): Promise<ListResponse<ActivityItem>> {
    const body = await apiClient.get<unknown>(buildActivitiesPath(params));
    return toListResponse(body, toActivityItem);
}

/** Row 3: the open follow-ups per bucket under a scope. */
export async function getActivityCounts(scope: ActivityScope): Promise<ActivityCounts> {
    return toActivityCounts(await apiClient.get<unknown>(buildCountsPath(scope)));
}

/** Row 5: create a follow-up; the `201` body is an `ActivityDetail`. */
export async function createActivity(request: ActivityCreateRequest): Promise<ActivityDetail> {
    const body = await apiClient.post<unknown>(ACTIVITIES_PATH, toCreateBody(request));
    return toActivityDetail(body, 'the body');
}

/** Rows 4, 7 and 10's path, `/api/v1/activities/{id}`; rows 8 and 9 add `/complete` or `/reopen`. */
export function activityPath(id: number): string {
    return `${ACTIVITIES_PATH}/${id}`;
}

/** Row 4: one follow-up, for the edit dialog — `notes` and the current `version` are on it, not the list item. */
export async function getActivity(id: number): Promise<ActivityDetail> {
    return toActivityDetail(await apiClient.get<unknown>(activityPath(id)), 'the body');
}

/**
 * Row 7 (F3-a Wave 2 contract `### ActivityPatchRequest`): `PATCH` the request as given — `version` plus
 * the changed members, which `followUpForm.ts`'s `toPatchRequest` chose (F3-a WI-2.2 plan Decision 3).
 * The `200` body, a no-op's included, is the follow-up as saved; its `version` is the next to send.
 */
export async function updateActivity(id: number, request: ActivityPatchRequest): Promise<ActivityDetail> {
    return toActivityDetail(await apiClient.patch<unknown>(activityPath(id), request), 'the body');
}

/**
 * Rows 8 and 9 (F3-a Wave 2 contract `### ActivityStatusChangeRequest`): exactly `{ version }`. A
 * follow-up already at the target status answers `200` unchanged (F3-a Wave 2 contract D-5), read the
 * same way.
 */
async function changeActivityStatus(
    id: number,
    change: 'complete' | 'reopen',
    version: number,
): Promise<ActivityDetail> {
    const body: ActivityStatusChangeRequest = { version };
    return toActivityDetail(await apiClient.post<unknown>(`${activityPath(id)}/${change}`, body), 'the body');
}

/** Row 8: mark the follow-up done → `done` set, `bucket: "completed"`. */
export function completeActivity(id: number, version: number): Promise<ActivityDetail> {
    return changeActivityStatus(id, 'complete', version);
}

/** Row 9: reopen the follow-up → `done: null`, the bucket computed again by the server. */
export function reopenActivity(id: number, version: number): Promise<ActivityDetail> {
    return changeActivityStatus(id, 'reopen', version);
}

/**
 * Row 10 (F3-a Wave 2 contract `### Row 10's query parameter`): `version` is the one query parameter,
 * sent in the path because `apiClient.delete` takes the path only. A `204` has no body.
 */
export async function deleteActivity(id: number, version: number): Promise<void> {
    await apiClient.delete(pathWithQuery(activityPath(id), [['version', version]]));
}

/**
 * Row 6: who may be assigned a follow-up on the record — every page, as one list (`readEveryPage`,
 * BUG-109, L-151), so no candidate past the first hundred is left out. A candidate listed on two pages is
 * kept once; a failing page, or a first page reporting more than `EVERY_PAGE_MAX` pages, rejects the
 * whole read, so the dialog never offers a partial list.
 */
export function listAssigneeCandidates(record: ActivityRecordRef): Promise<ListResponse<AssigneeCandidate>> {
    return readEveryPage(
        async (page) =>
            toListResponse(
                await apiClient.get<unknown>(buildCandidatesPath(record, page)),
                toAssigneeCandidate,
            ),
        { what: 'The assignee candidates', idOf: (candidate) => candidate.id },
    );
}
